/**
 * PaymentHistoryTab — Unified payment money trail for a lead/client.
 *
 * Surfaces all invoice/payment records in a single chronological list:
 *   - charge_now (manual)   → "Manual Charge"
 *   - auto-charged final    → "Auto-Charged"
 *   - prepay package        → "Prepay Package"
 *   - payment link final    → "Payment Link"
 *   - auto-charge failed    → "Charge Failed"
 *   - deposit               → "Deposit"
 *
 * Each row shows: type badge · date · amount · status badge · card detail · action
 * Actions: "Retry" for autoChargeFailed, "Charge" for any unpaid non-prepay invoice.
 */

import React, { useState, useEffect } from 'react';
import {
  collection, query, where, onSnapshot,
} from 'firebase/firestore';
import { db, auth } from '../../../../firebase';
import { useCompany } from '../../../../context/CompanyContext';
import {
  CreditCard, Send, DollarSign, Receipt,
  RotateCcw, Zap, ExternalLink,
} from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { toast } from 'sonner';
import { getDisplayName } from '../../../../lib/utils';
import { resolveRowAction } from '../../../../lib/invoiceFinancials';
import ChargeNowModal from '../../../../components/ChargeNowModal';
import BillingPolicyCard from './BillingPolicyCard';

// ── Helpers ────────────────────────────────────────────────────────────────────

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    // Handle Firestore Timestamp objects (have .seconds/.nanoseconds)
    const d = iso?.seconds ? new Date(iso.seconds * 1000) : new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch { return '—'; }
};

const fmt$ = (amt) => `$${(parseFloat(amt) || 0).toFixed(2)}`;

function getTypeConfig(inv) {
  const { invoiceType, source, paymentMethod, autoChargeFailed, stripePaymentLink } = inv;

  if (invoiceType === 'charge_now') {
    return source === 'manual'
      ? { label: 'Manual Charge',    cls: 'bg-violet-100 text-violet-700', Icon: DollarSign }
      : { label: 'Charge Now',       cls: 'bg-violet-100 text-violet-700', Icon: Zap };
  }
  if (invoiceType === 'prepay') {
    return { label: 'Prepay Package', cls: 'bg-orange-100 text-orange-700', Icon: Receipt };
  }
  if (invoiceType === 'deposit') {
    return { label: 'Deposit',        cls: 'bg-cyan-100 text-cyan-700',    Icon: Receipt };
  }
  if (autoChargeFailed) {
    return { label: 'Charge Failed',  cls: 'bg-amber-100 text-amber-700',  Icon: CreditCard };
  }
  if (paymentMethod === 'card_on_file') {
    return { label: 'Auto-Charged',   cls: 'bg-green-100 text-green-700',  Icon: CreditCard };
  }
  if (stripePaymentLink) {
    return { label: 'Payment Link',   cls: 'bg-blue-100 text-blue-700',    Icon: Send };
  }
  return { label: 'Invoice',          cls: 'bg-gray-100 text-gray-600',    Icon: Receipt };
}

function getStatusConfig(inv) {
  const { status, autoChargeFailed } = inv;
  if (autoChargeFailed && status !== 'paid')
    return { label: 'Charge Failed', cls: 'bg-amber-100 text-amber-700 border border-amber-200' };
  switch (status) {
    case 'paid':           return { label: 'Paid',      cls: 'bg-green-100 text-green-700 border border-green-200' };
    case 'sent':           return { label: 'Link Sent', cls: 'bg-blue-100 text-blue-700 border border-blue-200' };
    case 'pending':
    case 'pending_intent': return { label: 'Pending',   cls: 'bg-gray-100 text-gray-500 border border-gray-200' };
    case 'failed':         return { label: 'Failed',    cls: 'bg-red-100 text-red-700 border border-red-200' };
    default:               return { label: 'Pending',   cls: 'bg-gray-100 text-gray-500 border border-gray-200' };
  }
}

// ── Component ──────────────────────────────────────────────────────────────────

const PaymentHistoryTab = ({ client }) => {
  const { companyId } = useCompany();
  const [records, setRecords]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [chargeNowInvoice, setChargeNow] = useState(null);
  const [retrying, setRetrying]         = useState(null);

  // ── leadId resolution (mirrors JobsTab pattern) ───────────────────────────
  const leadId = client?.leadId || (!client?.clientId ? client?.id : null);
  const customerName = getDisplayName(client, 'Client');

  // ── Firestore real-time listener ──────────────────────────────────────────
  useEffect(() => {
    if (!companyId || !leadId) { setLoading(false); return; }

    const q = query(
      collection(db, 'companies', companyId, 'invoices'),
      where('leadId', '==', leadId),
    );

    const unsub = onSnapshot(q, (snap) => {
      const docs = snap.docs.map(d => ({ _docId: d.id, ...d.data() }));
      // Sort newest-first client-side (avoids needing a Firestore composite index)
      docs.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      setRecords(docs);
      setLoading(false);
    }, (err) => {
      console.error('[PaymentHistoryTab] Firestore error:', err);
      setLoading(false);
    });

    return unsub;
  }, [companyId, leadId]);

  // ── Retry charge handler (reuses charge-invoice endpoint) ─────────────────
  const handleRetry = async (inv) => {
    const id = inv.invoiceId || inv._docId;
    setRetrying(id);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const token = await auth.currentUser?.getIdToken(false);
      const res = await fetch(`${backendUrl}/api/payments/charge-invoice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ companyId, invoiceId: id }),
      });
      let data = {};
      try { data = await res.json(); } catch (_) {}
      if (res.ok && data.success) {
        toast.success(`Charged ${data.cardBrand} ••••${data.cardLast4} — ${fmt$(data.amount)}`);
      } else {
        toast.error(data.detail || 'Charge failed. Check card on file.');
      }
    } catch (err) {
      console.error('[PaymentHistoryTab] Retry error:', err);
      toast.error('Network error — please try again');
    } finally {
      setRetrying(null);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="space-y-6">
        <BillingPolicyCard client={client} />
        <div className="py-12 text-center text-gray-400 text-sm">
          Loading payment history…
        </div>
      </div>
    );
  }

  if (!records.length) {
    return (
      <div className="space-y-6">
        <BillingPolicyCard client={client} />
        <div className="py-14 text-center border-t border-gray-100 pt-8">
          <Receipt className="h-10 w-10 mx-auto mb-3 text-gray-300" />
          <p className="text-sm font-medium text-gray-500">No payment history yet</p>
          <p className="text-xs text-gray-400 mt-1">
            Charges, payment links, and prepay records will appear here.
          </p>
        </div>
      </div>
    );
  }

  // ── Summary totals ─────────────────────────────────────────────────────────
  const totalPaid       = records.filter(r => r.status === 'paid').reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const totalOutstanding = records.filter(r => r.status !== 'paid').reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);

  return (
    <div className="space-y-6">
      <BillingPolicyCard client={client} />

      <div className="space-y-3 pt-2 border-t border-gray-100">

      {/* Summary strip */}
      <div className="flex gap-3 text-sm">
        <div className="flex-1 bg-green-50 border border-green-200 rounded-lg px-4 py-2.5">
          <p className="text-xs text-green-600 font-medium">Total Collected</p>
          <p className="text-lg font-bold text-green-700 tabular-nums">{fmt$(totalPaid)}</p>
        </div>
        <div className="flex-1 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5">
          <p className="text-xs text-amber-600 font-medium">Outstanding</p>
          <p className="text-lg font-bold text-amber-700 tabular-nums">{fmt$(totalOutstanding)}</p>
        </div>
        <div className="flex items-center justify-end px-3">
          <p className="text-xs text-gray-400">{records.length} record{records.length !== 1 ? 's' : ''}</p>
        </div>
      </div>

      {/* Records list */}
      <div className="space-y-1.5" data-testid="payment-history-list">
        {records.map((inv) => {
          const rowId      = inv.invoiceId || inv._docId;
          const typeConf   = getTypeConfig(inv);
          const statusConf = getStatusConfig(inv);
          const isPaid     = inv.status === 'paid';
          // Centralized collection routing (lib/invoiceFinancials.resolveRowAction):
          //   'retry'  → failed AutoPay recovery (charge-invoice)
          //   'charge' → universal Charge Now (ChargeNowModal → charge-now)
          const rowAction  = resolveRowAction(inv);
          const canRetry   = rowAction === 'retry';
          const canCharge  = rowAction === 'charge';
          const isRetrying = retrying === rowId;

          return (
            <div
              key={rowId}
              className="flex items-center gap-3 px-4 py-3 rounded-lg border border-gray-100 bg-gray-50/70 hover:bg-white hover:border-gray-200 transition-colors"
              data-testid={`payment-row-${rowId}`}
            >
              {/* Type icon */}
              <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${typeConf.cls}`}>
                <typeConf.Icon className="h-3.5 w-3.5" />
              </div>

              {/* Type label + date + ref */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${typeConf.cls}`}>
                    {typeConf.label}
                  </span>
                  {inv.quoteId && (
                    <a
                      href={`/quotes/${inv.quoteId}?companyId=${companyId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[10px] text-blue-600 hover:text-blue-800 flex items-center gap-0.5"
                      data-testid={`payment-row-quote-link-${rowId}`}
                    >
                      Quote <ExternalLink className="h-2.5 w-2.5" />
                    </a>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  {fmtDate(inv.createdAt)}
                  {inv.description && (
                    <span className="ml-2 text-gray-400 truncate">· {inv.description}</span>
                  )}
                </p>
              </div>

              {/* Amount */}
              <div className="shrink-0 text-right w-20">
                <p className="text-sm font-semibold text-gray-900">{fmt$(inv.amount)}</p>
                {isPaid && inv.paidAt && (
                  <p className="text-[10px] text-gray-400">{fmtDate(inv.paidAt)}</p>
                )}
              </div>

              {/* Status */}
              <div className="shrink-0 w-24 flex justify-center">
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${statusConf.cls}`}>
                  {statusConf.label}
                </span>
              </div>

              {/* Card detail (paid via card) */}
              <div className="shrink-0 w-28 text-right hidden md:block">
                {inv.cardBrand && inv.cardLast4 && (
                  <p className="text-[10px] text-gray-500 flex items-center justify-end gap-0.5">
                    <CreditCard className="h-2.5 w-2.5" />
                    {inv.cardBrand} ••••{inv.cardLast4}
                  </p>
                )}
              </div>

              {/* Actions */}
              <div className="shrink-0 flex items-center gap-1">
                {canRetry && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-2 text-xs text-red-600 border-red-200 hover:bg-red-50 font-medium"
                    onClick={() => handleRetry(inv)}
                    disabled={isRetrying}
                    data-testid={`payment-row-retry-btn-${rowId}`}
                  >
                    <RotateCcw className={`h-3.5 w-3.5 mr-1 ${isRetrying ? 'animate-spin' : ''}`} />
                    Retry
                  </Button>
                )}
                {canCharge && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-2 text-xs text-green-700 border-green-300 hover:bg-green-50 font-medium"
                    onClick={() => setChargeNow(inv)}
                    data-testid={`payment-row-charge-btn-${rowId}`}
                  >
                    <Zap className="h-3.5 w-3.5 mr-1" />
                    Charge
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ChargeNowModal — opens inline when operator clicks Charge on a row */}
      <ChargeNowModal
        isOpen={!!chargeNowInvoice}
        onClose={() => setChargeNow(null)}
        companyId={companyId}
        leadId={leadId}
        customerName={customerName}
        invoice={chargeNowInvoice}
        onSuccess={() => setChargeNow(null)}
      />
      </div>
    </div>
  );
};

export default PaymentHistoryTab;
